import React, { useState, useCallback } from 'react';

import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import { DEFAULT_LAT, DEFAULT_LNG } from 'common/constants';

import LabelError from './LabelError';
import MapContainer from './MapContainer';

const ControlledMap = ({
    center = { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
    disableDefaultUI = false,
    error,
    heightMap,
    onChange,
    value,
    readOnly = false,
    title,
    zoom,
}) => {
    const [isZooming, setIsZooming] = useState(false);

    const onMarkerDragend = (_, marker) => {
        onChange({
            latitude: marker.position.lat(),
            longitude: marker.position.lng(),
        });
    };

    // Debounce para handleOnPositionChanged para evitar disparos excesivos
    const debouncedHandleOnPositionChanged = useCallback(
        debounce((mapProps, map) => {
            if (!isZooming) {
                onChange({
                    latitude: map.center.lat(),
                    longitude: map.center.lng(),
                });
            }
        }, 200), // 200ms debounce
        [isZooming, onChange],
    );

    // Detectar inicio de cambio de zoom
    const handleOnZoomChanged = () => {
        setIsZooming(true);
    };

    // Disparar onChange cuando el mapa está inactivo después de un cambio de zoom
    const handleOnIdle = (mapProps, map) => {
        if (isZooming) {
            onChange({
                latitude: map.center.lat(),
                longitude: map.center.lng(),
            });
            setIsZooming(false);
        }
    };

    const latitude =
        value?.latitude &&
        -90 < parseFloat(value?.latitude) &&
        parseFloat(value?.latitude) < 90
            ? value?.latitude
            : center.lat;

    const longitude =
        value?.longitude &&
        -180 < parseFloat(value?.longitude) &&
        parseFloat(value?.longitude) < 180
            ? value?.longitude
            : center.lng;

    const markers = [
        {
            draggable: !readOnly, // Respetar readOnly para el marcador
            lat: latitude,
            lng: longitude,
            name: title,
            onDragend: onMarkerDragend,
            title: title,
        },
    ];

    return (
        <>
            <MapContainer
                center={{ lat: latitude, lng: longitude }}
                disableDefaultUI={disableDefaultUI}
                handleOnPositionChanged={debouncedHandleOnPositionChanged}
                handleOnZoomChanged={handleOnZoomChanged}
                handleOnIdle={handleOnIdle}
                heightMap={heightMap}
                markers={markers}
                readOnly={readOnly}
                zoom={zoom}
            />

            {error?.message && (
                <LabelError type="error" fontSize="13px">
                    {error?.message}
                </LabelError>
            )}
        </>
    );
};

ControlledMap.propTypes = {
    center: PropTypes.object,
    disableDefaultUI: PropTypes.bool,
    error: PropTypes.object,
    heightMap: PropTypes.string,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    title: PropTypes.string,
    value: PropTypes.object,
    zoom: PropTypes.number,
};

export default ControlledMap;
